import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Mic, Image, Video, BarChart3, Users, Shield, Lock, UserCheck, ChevronRight, ArrowLeft } from 'lucide-react';

const CreateContentModal = ({ currentTheme, onClose, onSelect, t }) => {
  const [showPrivacySelector, setShowPrivacySelector] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [privacy, setPrivacy] = useState('circle');

  const contentTypes = [
    {
      id: 'text',
      icon: FileText,
      label: t?.('identityCircle.create.text') || 'Düşünce Paylaş',
      color: 'from-blue-400 to-blue-600'
    },
    {
      id: 'voice',
      icon: Mic,
      label: t?.('identityCircle.create.voice') || 'Sesli Not',
      color: 'from-green-400 to-green-600'
    },
    {
      id: 'image',
      icon: Image,
      label: t?.('identityCircle.create.image') || 'Medya Paylaş',
      color: 'from-red-400 to-red-600'
    },
    {
      id: 'stats',
      icon: BarChart3,
      label: t?.('identityCircle.create.stats') || 'İstatistik Paylaş',
      color: 'from-blue-400 to-blue-600'
    }
  ];

  const privacyOptions = [
    {
      id: 'circle',
      icon: Users,
      label: t?.('identityCircle.privacy.circle') || 'Circle',
      description: t?.('identityCircle.privacy.circleDesc') || 'Tüm circle üyelerine görünür',
      gradient: 'from-green-500 to-emerald-600',
      emoji: '👥'
    },
    {
      id: 'closeCircle',
      icon: UserCheck,
      label: t?.('identityCircle.privacy.closeCircle') || 'Yakın Circle',
      description: t?.('identityCircle.privacy.closeCircleDesc') || 'Sadece yakın arkadaşlarına görünür',
      gradient: 'from-amber-500 to-orange-600',
      emoji: '❤️'
    },
    {
      id: 'selected',
      icon: Shield,
      label: t?.('identityCircle.privacy.selected') || 'Seçili Kişiler',
      description: t?.('identityCircle.privacy.selectedDesc') || 'Belirlediğin kişilere görünür',
      gradient: 'from-blue-500 to-cyan-600',
      emoji: '👤'
    },
    {
      id: 'vault',
      icon: Lock,
      label: t?.('identityCircle.privacy.vault') || 'Dijital Kasa',
      description: t?.('identityCircle.privacy.vaultDesc') || 'Sadece sana görünür, şifrelenmis',
      gradient: 'from-purple-600 to-violet-700',
      emoji: '🔒'
    }
  ];

  const handleContentTypeSelect = (type) => {
    setSelectedContentType(type);
    setShowPrivacySelector(true);
  };

  const handlePrivacySelect = (privacyType) => {
    setPrivacy(privacyType);
  };

  const handleConfirm = () => {
    if (selectedContentType && privacy) {
      onSelect(selectedContentType, privacy);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className={`${currentTheme?.cardBg || 'bg-white'} rounded-2xl w-full max-w-md shadow-2xl overflow-hidden`}
      >
        <AnimatePresence mode="wait">
          {!showPrivacySelector ? (
            <motion.div
              key="content-selection"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <div className={`flex items-center justify-between p-4 border-b ${currentTheme?.border || 'border-gray-200'}`}>
                <h2 className={`text-xl font-bold ${currentTheme?.textPrimary || 'text-gray-900'}`}>
                  {t?.('identityCircle.createContent') || 'Create Content'}
                </h2>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-full ${currentTheme?.hover || 'hover:bg-gray-100'} transition-colors`}
                >
                  <X size={20} className={`${currentTheme?.textSecondary || 'text-gray-500'}`} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 p-6">
                {contentTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <motion.button
                      key={type.id}
                      onClick={() => handleContentTypeSelect(type.id)}
                      className={`p-6 rounded-xl bg-gradient-to-br ${type.color} text-white flex flex-col items-center justify-center space-y-3 hover:shadow-lg transition-all`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon size={32} />
                      <span className="text-sm font-medium text-center">{type.label}</span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Privacy hint at bottom */}
              <div className={`px-6 pb-6 flex items-center justify-center space-x-2 ${currentTheme?.textSecondary || 'text-gray-500'} bg-gray-50 dark:bg-gray-800`}>
                <Lock size={16} />
                <span className="text-sm">
                  {t?.('identityCircle.create.privacyHint') || 'Dijital Kasa'}
                </span>
                <span className="text-xs opacity-75">
                  {t?.('identityCircle.create.securityNote') || 'Kişisel notlarına git'}
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="privacy-selection"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
            >
              <div className={`flex items-center justify-between p-4 border-b ${currentTheme?.border || 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowPrivacySelector(false)}
                    className={`p-1 rounded-full ${currentTheme?.hover || 'hover:bg-gray-100'} transition-colors`}
                  >
                    <ChevronRight size={20} className={`${currentTheme?.textSecondary || 'text-gray-500'} rotate-180`} />
                  </button>
                  <h2 className={`text-xl font-bold ${currentTheme?.textPrimary || 'text-gray-900'}`}>
                    {t?.('identityCircle.privacy.title') || 'Gizlilik Ayarları'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-full ${currentTheme?.hover || 'hover:bg-gray-100'} transition-colors`}
                >
                  <X size={20} className={`${currentTheme?.textSecondary || 'text-gray-500'}`} />
                </button>
              </div>

              <div className="p-6">
                <div className="text-center mb-6">
                  <div className={`mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center mb-3`}>
                    <Shield size={24} className="text-white" />
                  </div>
                  <h3 className={`text-lg font-semibold ${currentTheme?.textPrimary || 'text-gray-900'} mb-1`}>
                    {t?.('identityCircle.privacy.subtitle') || 'İçeriğini kimler görebilsin?'}
                  </h3>
                </div>

                <div className="space-y-3">
                  {privacyOptions.map(option => {
                    const Icon = option.icon;
                    const isSelected = privacy === option.id;
                    
                    return (
                      <motion.button
                        key={option.id}
                        onClick={() => handlePrivacySelect(option.id)}
                        className={`w-full flex items-center space-x-4 p-4 rounded-xl border transition-all ${
                          isSelected 
                            ? 'bg-gradient-to-r from-green-400 to-blue-500 text-white border-transparent shadow-lg' 
                            : `${currentTheme?.cardBg === 'bg-gray-800' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} ${currentTheme?.border || 'border-gray-200'}`
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/20' : (currentTheme?.cardBg === 'bg-gray-800' ? 'bg-gray-600' : 'bg-white')}`}>
                          <Icon size={20} className={isSelected ? 'text-white' : (currentTheme?.textPrimary || 'text-gray-700')} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className={`font-medium ${isSelected ? 'text-white' : currentTheme?.textPrimary || 'text-gray-900'}`}>
                            {option.label}
                          </p>
                          <p className={`text-sm ${isSelected ? 'text-white/80' : currentTheme?.textSecondary || 'text-gray-600'}`}>
                            {option.description}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-3 h-3 bg-white rounded-full" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <button
                  onClick={handleConfirm}
                  className="w-full mt-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium rounded-xl transition-all"
                >
                  {t?.('identityCircle.create.confirm') || 'Devam Et'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CreateContentModal;
